import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Sparkles, Zap, Trophy } from 'lucide-react';
import { useQuest } from '@/context/QuestContext';
import { track } from '@/lib/api';
import { BrandBanner } from '@/components/BrandLogo';

export default function QuizIntro() {
  const navigate = useNavigate();
  const { session } = useQuest();

  if (!session.player) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-bg p-6">
        <div className="card-soft p-8 max-w-md text-center">
          <p className="text-body">Please complete the player setup first.</p>
          <Link to="/setup" className="btn-primary inline-block mt-4">Go to Setup</Link>
        </div>
      </div>
    );
  }

  const start = () => {
    track('quiz_started', { grade: session.player.grade });
    navigate('/quiz');
  };

  const rules = [
    { i: Sparkles, c: '#FFD600', t: 'Choose what feels most like you.', s: 'There are no right or wrong answers.' },
    { i: Zap,      c: '#0055FF', t: 'Move at your own pace.',           s: 'Each answer quietly shapes your Future Profile.' },
    { i: Trophy,   c: '#00D95F', t: 'Unlock your Career Avatar.',        s: 'At the end, you’ll discover your top zones, strengths, skills, and project idea.' },
  ];

  return (
    <div className="min-h-screen gradient-bg grain">
      <BrandBanner />
      <nav className="sticky top-0 z-30 glass">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/setup" data-testid="back-to-setup" className="flex items-center gap-2 text-sm font-bold text-muted-ink hover:text-ink">
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back
          </Link>
          <div className="font-display font-extrabold text-ink text-sm">Quiz Intro</div>
          <div className="w-12" />
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0055FF]">Hey {session.player.student_name.split(' ')[0]} 👋</p>
          <h1 className="font-display font-black text-ink text-3xl sm:text-5xl mt-2 tracking-tighter">How the Career Fit Assessment works</h1>
          <p className="text-body mt-3">3 simple rules. 24 quick questions. 1 personalized future profile.</p>
        </motion.div>

        <div className="mt-8 space-y-4">
          {rules.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
              className="card-soft p-5 sm:p-6 flex items-start gap-4"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${r.c}1A`, color: r.c }}>
                <r.i strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-extrabold text-ink text-lg">{i + 1}. {r.t}</div>
                <div className="text-sm text-muted-ink mt-1">{r.s}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 card-dark p-6 sm:p-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD600]">4 Levels · 24 Questions</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {[
              { n: 1, t: 'Know Yourself', c: '#FFD600' },
              { n: 2, t: 'Decode Your Interests', c: '#0055FF' },
              { n: 3, t: 'Build Your Skill Stack', c: '#00D95F' },
              { n: 4, t: 'Unlock Your Future Path', c: '#FF5A00' },
            ].map((l) => (
              <div key={l.n} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: l.c }}>Level {l.n}</div>
                <div className="font-display font-extrabold mt-1">{l.t}</div>
              </div>
            ))}
          </div>
        </div>

        <button onClick={start} data-testid="start-level-1-button" className="btn-primary w-full mt-8 inline-flex items-center justify-center gap-2">
          Start Level 1 <ArrowRight className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
